import axios from 'axios';
import { api, HttpError } from './api';
import { AppRole, normalizeAppRole } from '../utils/roleUtils';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: AppRole;
  is_active: boolean;
  company_id: string;
  created_at: string;
  module_access: string[];
}

export interface CreateUserData {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  role: AppRole;
  module_access: string[];
}

export interface UpdateUserData {
  first_name?: string;
  last_name?: string;
  role?: AppRole;
  is_active?: boolean;
  module_access?: string[];
}

export interface UserFilters {
  role?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UsersResponse {
  users: User[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface ModuleAccess {
  id: string;
  name: string;
  description: string;
}

class UserManagementService {
  private normalizeUser(user: any): User {
    return {
      ...user,
      role: normalizeAppRole(user?.role),
    } as User;
  }

  // Get all users with pagination and filters
  async getUsers(filters: UserFilters): Promise<UsersResponse> {
    try {
      const response = await api.get('/users', { params: filters });
      const data = response.data.data;
      return {
        ...data,
        users: Array.isArray(data?.users) ? data.users.map((u: any) => this.normalizeUser(u)) : [],
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const msg = String(
          error.response?.data?.message ??
            error.response?.data?.error?.message ??
            error.message ??
            ''
        );
        if (/organization|org|tenant|context|scope/i.test(msg)) {
          throw new HttpError(
            'Organization context is required. Choose an organization in the workspace switcher, or complete CA workspace setup.',
            400
          );
        }
      }
      throw error;
    }
  }

  // Get a specific user by ID
  async getUserById(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return this.normalizeUser(response.data.data.user);
  }

  // Create a new user
  async createUser(userData: CreateUserData): Promise<User> {
    const response = await api.post('/users', {
      ...userData,
      role: userData.role,
    });
    return this.normalizeUser(response.data.data.user);
  }

  // Update an existing user
  async updateUser(id: string, userData: UpdateUserData): Promise<User> {
    const response = await api.put(`/users/${id}`, {
      ...userData,
      ...(userData.role ? { role: userData.role } : {}),
    });
    return this.normalizeUser(response.data.data.user);
  }

  // Delete a user
  async deleteUser(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }

  // Update user module access
  async updateModuleAccess(id: string, moduleAccess: string[]): Promise<User> {
    const response = await api.put(`/users/${id}/module-access`, { module_access: moduleAccess });
    return response.data.data.user;
  }

  // Toggle user status (activate/deactivate)
  async toggleUserStatus(id: string): Promise<User> {
    const response = await api.put(`/users/${id}/status`);
    return this.normalizeUser(response.data.data.user);
  }

  // Get available modules for access control
  getAvailableModules(): ModuleAccess[] {
    return [
      { id: 'dashboard', name: 'Dashboard', description: 'Main dashboard and overview' },
      { id: 'transactions', name: 'Financial Transactions', description: 'Manage income and expenses' },
      { id: 'gst', name: 'GST Management', description: 'GST filing and compliance' },
      { id: 'reports', name: 'Reports & Analytics', description: 'Financial reports and analysis' },
      { id: 'advanced_analytics', name: 'Advanced Analytics', description: 'AI-powered financial insights and predictive analytics' },
      { id: 'tasks', name: 'Task Management', description: 'Create and manage tasks' },
      { id: 'documents', name: 'Document Management', description: 'File and document storage' },
      { id: 'users', name: 'User Management', description: 'Manage users and permissions' },
      { id: 'settings', name: 'System Settings', description: 'Company and system configuration' }
    ];
  }

  // Get default module access based on role
  getDefaultModuleAccess(role: string): string[] {
    switch (role) {
      case 'admin':
      case 'owner':
        return ['dashboard', 'transactions', 'gst', 'reports', 'advanced_analytics', 'tasks', 'documents', 'users', 'settings'];
      case 'member':
        return ['dashboard', 'gst', 'reports', 'advanced_analytics', 'documents'];
      case 'viewer':
        return ['dashboard', 'reports'];
      default:
        return ['dashboard'];
    }
  }

  // Check if user has access to a specific module
  hasModuleAccess(user: User, moduleId: string): boolean {
    // Admins have access to all modules
    if (user.role === 'admin' || user.role === 'owner') {
      return true;
    }

    return user.module_access.includes(moduleId);
  }

  // Get role display name
  getRoleDisplayName(role: string): string {
    switch (role) {
      case 'owner': return 'Owner';
      case 'admin': return 'Administrator';
      case 'member': return 'Member';
      case 'viewer': return 'Viewer';
      default: return role;
    }
  }

  // Get role color for badges
  getRoleColor(role: string): string {
    switch (role) {
      case 'owner': return 'bg-indigo-100 text-indigo-800';
      case 'admin': return 'bg-red-100 text-red-800';
      case 'member': return 'bg-blue-100 text-blue-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  // Validate user data
  validateUserData(userData: CreateUserData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!userData.email || !userData.email.includes('@')) {
      errors.push('Valid email is required');
    }

    if (!userData.first_name || userData.first_name.trim().length < 1) {
      errors.push('First name is required');
    }

    if (!userData.last_name || userData.last_name.trim().length < 1) {
      errors.push('Last name is required');
    }

    if (!userData.password || userData.password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!userData.role) {
      errors.push('Role is required');
    }

    if (!userData.module_access || userData.module_access.length === 0) {
      errors.push('At least one module access is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export const userManagementService = new UserManagementService();







